'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import GXGLayout from '@/components/GXGLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const CoinIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b"><circle cx="12" cy="12" r="10"/></svg>;
const ClockIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const UsersIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const UploadIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;

interface Bounty {
  id: string;
  title: string;
  description: string;
  reward_label: string;
  reward_coins: number;
  difficulty: string;
  expires_at: string | null;
  participant_count: number;
  proof_required: boolean;
  status: string;
  created_at: string;
}

interface BountySubmission {
  id: string;
  bounty_id: string;
  status: string;
}

const diffColor = (d: string) => {
  if (d === 'Legendary') return 'badge-gold';
  if (d === 'Hard') return 'badge-red';
  if (d === 'Medium') return 'badge-blue';
  return 'badge-green';
};

const diffGradient = (d: string) => {
  if (d === 'Legendary') return 'from-yellow-900 to-amber-900';
  if (d === 'Hard') return 'from-red-900 to-orange-900';
  if (d === 'Medium') return 'from-blue-900 to-indigo-900';
  return 'from-green-900 to-teal-900';
};

function timeLeft(expiresAt: string | null): string {
  if (!expiresAt) return 'No expiry';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} left`;
  return `${hours}h left`;
}

export default function BountyBoardPage() {
  const { user } = useAuth();
  const [bounties, setBounties] = useState<Bounty[]>([]);
  const [completedBounties, setCompletedBounties] = useState<Bounty[]>([]);
  const [submissions, setSubmissions] = useState<BountySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [proofModal, setProofModal] = useState<Bounty | null>(null);
  const [proofNote, setProofNote] = useState('');
  const [proofLinks, setProofLinks] = useState<string[]>(['']);
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data: activeBounties } = await supabase
        .from('bounties')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      const { data: archivedBounties } = await supabase
        .from('bounties')
        .select('*')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(6);

      setBounties(activeBounties || []);
      setCompletedBounties(archivedBounties || []);

      if (user?.id) {
        const { data: subs } = await supabase
          .from('bounty_submissions')
          .select('id, bounty_id, status')
          .eq('user_id', user.id);
        setSubmissions(subs || []);
      }
    } catch (err) {
      console.error('Load bounties error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getSubmission = (id: string) => submissions.find(s => s.bounty_id === id);

  const handleClaim = (bounty: Bounty) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    setProofModal(bounty);
    setProofNote('');
    setProofLinks(['']);
    setProofFiles([]);
    setSubmitMsg('');
    setUploadProgress('');
  };

  const addLink = () => setProofLinks(prev => [...prev, '']);
  const removeLink = (i: number) => setProofLinks(prev => prev.filter((_, idx) => idx !== i));
  const updateLink = (i: number, val: string) => setProofLinks(prev => prev.map((l, idx) => idx === i ? val : l));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setProofFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (i: number) => setProofFiles(prev => prev.filter((_, idx) => idx !== i));

  const submitBounty = async () => {
    if (!user?.id || !proofModal) return;
    setSubmitting(true);
    setSubmitMsg('');
    setUploadProgress('');

    try {
      const supabase = createClient();

      // Insert submission
      const { data: sub, error: subErr } = await supabase
        .from('bounty_submissions')
        .insert({
          bounty_id: proofModal.id,
          user_id: user.id,
          status: 'pending_review',
          player_notes: proofNote || null,
          reward_coins: proofModal.reward_coins,
          proof_count_submitted: proofFiles.length + proofLinks.filter(l => l.trim()).length,
        })
        .select('id')
        .single();

      if (subErr) throw new Error(subErr.message);

      let proofNum = 1;

      // Upload files to mission-proofs bucket
      for (const file of proofFiles) {
        setUploadProgress(`Uploading ${proofNum}/${proofFiles.length}...`);
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${user.id}/${proofModal.id}/${Date.now()}_${safeName}`;
        const { error: uploadErr } = await supabase.storage
          .from('mission-proofs')
          .upload(path, file, { cacheControl: '3600', upsert: false });

        if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

        const { data: { publicUrl } } = supabase.storage.from('mission-proofs').getPublicUrl(path);

        await supabase.from('mission_proofs').insert({
          submission_id: sub.id,
          user_id: user.id,
          file_url: publicUrl,
          file_type: file.type.startsWith('video') ? 'video' : 'image',
          file_name: file.name,
          proof_number: proofNum,
          uploaded_at: new Date().toISOString(),
        });
        proofNum++;
      }

      // Save external links
      for (const link of proofLinks.filter(l => l.trim())) {
        await supabase.from('mission_proofs').insert({
          submission_id: sub.id,
          user_id: user.id,
          external_url: link.trim(),
          file_type: 'link',
          file_name: 'External Link',
          proof_number: proofNum,
          uploaded_at: new Date().toISOString(),
        });
        proofNum++;
      }

      // Player notification
      await supabase.from('notifications').insert({
        user_id: user.id,
        title: 'Bounty Submitted! 🎯',
        message: `Your claim for "${proofModal.title}" is pending review.`,
        type: 'info',
        priority: 'normal',
      });

      // Admin notification
      await supabase.from('admin_notifications').insert({
        title: 'New Bounty Claim',
        message: `A player submitted proof for bounty: "${proofModal.title}"`,
        type: 'info',
        entity_type: 'bounty_submission',
        entity_id: sub.id,
        read: false,
      });

      setSubmissions(prev => [...prev, { id: sub.id, bounty_id: proofModal.id, status: 'pending_review' }]);
      setSubmitMsg('Bounty claim submitted for review!');
      setTimeout(() => setProofModal(null), 1500);
    } catch (err: any) {
      setSubmitMsg(`Error: ${err?.message}`);
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">
            Bounty <span className="gxg-gradient-text">Board</span>
          </h1>
          <p className="text-white/50 text-sm">Community challenges. Rare rewards. Legendary status.</p>
        </div>

        {/* Active Bounties */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg text-white">Active Bounties</h2>
            {loading ? (
              <svg className="animate-spin w-4 h-4 text-gxg-purple2" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            ) : (
              <span className="badge-green">{bounties.length} Active</span>
            )}
          </div>

          {!loading && bounties.length === 0 && (
            <div className="glass-card p-10 text-center text-white/30">
              <div className="text-4xl mb-3">🎯</div>
              <div className="text-sm">No active bounties right now. Check back soon!</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bounties.map(bounty => {
              const sub = getSubmission(bounty.id);
              const isPending = sub?.status === 'pending_review';
              const isApproved = sub?.status === 'approved';
              const needsMore = sub?.status === 'needs_more_proof';

              return (
                <div key={bounty.id} className="glass-card-hover p-5 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${diffGradient(bounty.difficulty)}`} />
                  <div className="pl-3">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-display font-bold text-base text-white">{bounty.title}</h3>
                      <div className="flex flex-col items-end gap-1">
                        <span className={diffColor(bounty.difficulty)}>{bounty.difficulty}</span>
                        {bounty.proof_required && (
                          <span className="flex items-center gap-1 text-[9px] text-gxg-purple2 bg-purple-500/15 border border-purple-500/25 rounded px-1.5 py-0.5">
                            <UploadIcon /> Proof Required
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-white/50 mb-3">{bounty.description}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-white/40 mb-4">
                      <span className="flex items-center gap-1"><ClockIcon /> {timeLeft(bounty.expires_at)}</span>
                      <span className="flex items-center gap-1"><UsersIcon /> {bounty.participant_count ?? 0} participants</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-gxg-gold text-sm font-mono font-semibold">
                        <CoinIcon /> {bounty.reward_label || `${bounty.reward_coins} coins`}
                      </div>
                      {isApproved ? (
                        <span className="badge-green text-xs">✓ Completed</span>
                      ) : isPending ? (
                        <span className="badge-gold text-xs">⏳ Pending Review</span>
                      ) : needsMore ? (
                        <button onClick={() => handleClaim(bounty)} className="text-sm px-4 py-2 rounded-lg btn-gxg-pink">
                          Add More Proof
                        </button>
                      ) : (
                        <button onClick={() => handleClaim(bounty)} className="text-sm px-4 py-2 rounded-lg btn-gxg-primary">
                          {bounty.proof_required ? 'Claim + Submit Proof' : 'Claim Bounty'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Completed Bounties */}
        {completedBounties.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg text-white">Completed Bounties</h2>
              <span className="badge-purple">{completedBounties.length} Completed</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {completedBounties.map(bounty => (
                <div key={bounty.id} className="glass-card p-4 opacity-60">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <h3 className="font-semibold text-sm text-white/70">{bounty.title}</h3>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gxg-gold font-mono">{bounty.reward_label || `${bounty.reward_coins} coins`}</span>
                    <span className="text-white/35">{new Date(bounty.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Proof Submission Modal */}
      {proofModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="glass-card p-6 w-full max-w-lg neon-border-purple my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg text-white">{proofModal.title}</h2>
                <div className="text-xs text-white/40 mt-0.5">Submit proof to claim this bounty</div>
              </div>
              <button onClick={() => setProofModal(null)} className="text-white/40 hover:text-white">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-gxg-gold/10 border border-gxg-gold/20">
              <CoinIcon />
              <span className="text-sm font-mono font-bold text-gxg-gold">{proofModal.reward_label || `${proofModal.reward_coins} coins`}</span>
            </div>

            {/* File Upload */}
            <div className="mb-4">
              <label className="text-xs text-white/50 mb-1.5 block">Upload Proof Files (images, videos, GIFs)</label>
              <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-white/15 rounded-lg cursor-pointer hover:border-purple-500/40 hover:bg-purple-500/5 transition-all">
                <span className="text-xs text-white/40">Click to choose files or drag & drop</span>
                <span className="text-[10px] text-white/25 mt-1">MP4, WebM, MOV, GIF, JPG, PNG — max 100MB each</span>
                <input type="file" multiple accept="video/mp4,video/webm,video/quicktime,.gif,image/jpeg,image/png,image/gif" className="hidden" onChange={handleFileChange} />
              </label>
              {proofFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {proofFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1">
                      <span className="text-white/70 truncate flex-1">{f.name}</span>
                      <button onClick={() => removeFile(i)} className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* External Links */}
            <div className="mb-4">
              <label className="text-xs text-white/50 mb-1.5 block">Proof Links (YouTube, Twitch, Google Drive, etc.)</label>
              <div className="space-y-2">
                {proofLinks.map((link, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="url"
                      className="gxg-input flex-1 text-sm"
                      placeholder="https://youtube.com/watch?v=..."
                      value={link}
                      onChange={e => updateLink(i, e.target.value)}
                    />
                    {proofLinks.length > 1 && (
                      <button onClick={() => removeLink(i)} className="text-red-400 hover:text-red-300 px-2">×</button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addLink} className="text-xs text-gxg-purple2 hover:text-white mt-1.5 transition-colors">+ Add another link</button>
            </div>

            {/* Notes */}
            <div className="mb-5">
              <label className="text-xs text-white/50 mb-1.5 block">Completion Notes</label>
              <textarea
                className="gxg-input resize-none h-20 text-sm"
                placeholder="Describe how you completed this bounty..."
                value={proofNote}
                onChange={e => setProofNote(e.target.value)}
              />
            </div>

            {uploadProgress && (
              <div className="mb-3 text-xs text-gxg-purple2 flex items-center gap-2">
                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {uploadProgress}
              </div>
            )}

            {submitMsg && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                submitMsg.startsWith('Error') ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-green-500/10 border border-green-500/30 text-green-400'
              }`}>{submitMsg}</div>
            )}

            <div className="flex gap-3">
              <button
                onClick={submitBounty}
                disabled={submitting}
                className={`btn-gxg-primary px-5 py-2.5 rounded-lg text-sm flex-1 ${submitting ? 'opacity-50' : ''}`}
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
              <button onClick={() => setProofModal(null)} className="btn-gxg-outline px-5 py-2.5 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </GXGLayout>
  );
}
